import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  PRODUCTS as FALLBACK_PRODUCTS,
  CATEGORIES as FALLBACK_CATEGORIES,
} from "@/data/products";
import { api } from "@/lib/api";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [products, setProducts] = useState(FALLBACK_PRODUCTS);
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const [heroSlides, setHeroSlides] = useState([]);
  const [wishlist, setWishlist] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("cambroo_wishlist") || "[]");
    } catch {
      return [];
    }
  });
  const [emirate, setEmirate] = useState(() => localStorage.getItem("cambroo_emirate") || "Dubai");

  const toggleWishlist = (id) =>
    setWishlist((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  useEffect(() => {
    localStorage.setItem("cambroo_wishlist", JSON.stringify(wishlist));
  }, [wishlist]);

  useEffect(() => {
    localStorage.setItem("cambroo_emirate", emirate);
  }, [emirate]);

  useEffect(() => {
    api
      .get("/products")
      .then((data) => {
        if (Array.isArray(data?.products)) setProducts(data.products);
        if (data?.categories?.length) setCategories(data.categories);
        if (data?.hero_slides?.length) setHeroSlides(data.hero_slides);
      })
      .catch(() => {});
  }, []);

  const addItem = (id, qty = 1) =>
    setItems((prev) => {
      const found = prev.find((i) => i.id === id);
      if (found) return prev.map((i) => (i.id === id ? { ...i, qty: i.qty + qty } : i));
      return [...prev, { id, qty }];
    });

  const setQty = (id, qty) =>
    setItems((prev) =>
      qty <= 0 ? prev.filter((i) => i.id !== id) : prev.map((i) => (i.id === id ? { ...i, qty } : i))
    );

  const removeItem = (id) => setItems((prev) => prev.filter((i) => i.id !== id));
  const clearCart = () => setItems([]);

  const detailed = useMemo(
    () =>
      items
        .map((i) => ({ ...products.find((p) => p.id === i.id), qty: i.qty }))
        .filter((i) => i.name),
    [items, products]
  );
  const subtotal = useMemo(() => detailed.reduce((s, i) => s + i.price * i.qty, 0), [detailed]);
  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);

  return (
    <CartContext.Provider
      value={{
        items,
        detailed,
        subtotal,
        count,
        addItem,
        setQty,
        removeItem,
        clearCart,
        cartOpen,
        setCartOpen,
        checkoutOpen,
        setCheckoutOpen,
        products,
        categories,
        heroSlides,
        wishlist,
        toggleWishlist,
        emirate,
        setEmirate,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
